import { useEffect, useRef, useState } from 'react';

const features = [
  {
    icon: 'ri-dashboard-3-line',
    title: 'リアルタイムダッシュボード',
    description: '入出金・売上・請求情報をリアルタイムで集計し、経営状況を即座に把握。意思決定のスピードを向上させます。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '高セキュリティ設計',
    description: '金融グレードの暗号化とアクセス制御により、重要な経理データを安全に保護。コンプライアンス対応も万全です。',
  },
  {
    icon: 'ri-git-branch-line',
    title: '柔軟なカスタマイズ',
    description: '業種・規模を問わず、お客様の業務フローに合わせたカスタマイズが可能。既存システムとのAPI連携にも対応。',
  },
  {
    icon: 'ri-cloud-line',
    title: 'クラウドネイティブ',
    description: 'インフラ管理不要のクラウドネイティブ設計。安定した運用を前提に、いつでもどこでもアクセスできる環境を整えます。',
  },
  {
    icon: 'ri-file-chart-line',
    title: '自動レポート生成',
    description: '月次・年次の財務レポートを自動生成。税務申告や監査対応に必要な書類を効率的に作成できます。',
  },
  {
    icon: 'ri-team-line',
    title: 'マルチテナント対応',
    description: '複数企業・複数拠点のデータを一括管理。グループ企業の連結経理にも対応した強力なマルチテナント機能。',
  },
];

export default function FeaturesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" className="bg-white py-24 md:py-32" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          className={`flex flex-col lg:flex-row gap-12 items-start mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex-1">
            <span className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-3 block">Features</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
              システムの<br />
              <span className="text-[#0ea5e9]">特徴</span>
            </h2>
          </div>
          <div className="flex-1">
            <p className="text-gray-500 text-sm leading-relaxed mt-2 lg:mt-8">
              最新のクラウド技術を活用し、経理業務のあらゆる課題を解決します。
              セキュリティ・スケーラビリティ・使いやすさを兼ね備えたシステムで、
              企業の成長を力強くサポートします。
            </p>
          </div>
        </div>

        {/* Feature image */}
        <div
          className={`mb-12 rounded-2xl overflow-hidden border border-gray-100 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <img
            src="https://readdy.ai/api/search-image?query=modern%20cloud%20accounting%20software%20dashboard%20interface%20showing%20financial%20charts%20revenue%20graphs%20data%20analytics%20clean%20professional%20design%20light%20background%20blue%20accent%20colors%20business%20intelligence&width=1200&height=400&seq=features-banner-001&orientation=landscape"
            alt="システム特徴イメージ"
            className="w-full h-48 md:h-64 object-cover object-top"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div
              key={f.title}
              className={`group flex gap-4 p-5 rounded-xl border border-gray-100 hover:border-[#38bdf8]/40 hover:bg-[#f0f9ff]/50 transition-all duration-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 80 + 300}ms` }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#f0f9ff] shrink-0 group-hover:bg-[#e0f2fe] transition-colors">
                <i className={`${f.icon} text-[#0ea5e9] text-lg`} />
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold text-sm mb-1.5">{f.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{f.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
